import type { TableColumnsType } from "antd";
import amazonIcon from "@assets/icons/AmazonIcon.png";
import ShopifyIcon from "@assets/icons/Shopify.png";
import tiktokIcon from "@assets/img/TikTok.png";
import { DataType } from "./model";

export function getColumns(
  _openConfigModal: (e: React.MouseEvent<HTMLButtonElement>) => void,
  visibleMetrics?: {
    impressions: boolean;
    clicks: boolean;
    orders: boolean;
    cvr: boolean;
    sales: boolean;
    salesShare: boolean;
    spend: boolean;
    acos: boolean;
    roi: boolean;
    cpc: boolean;
    cac: boolean;
  },
): TableColumnsType<DataType> {
  const allColumns = [
    {
      title: "Shop / Channel",
      dataIndex: "marketplace",
      className: "header-marketplace",
      width: "300px",
      onHeaderCell: () => ({
        style: {
          backgroundColor: "#2E2E2E",
          color: "#ffffff99",
          borderTopLeftRadius: 12,
          paddingLeft: "50px",
        },
      }),
      onCell: () => ({
        style: {
          display: "flex",
          alignItems: "center",
          gap: "8px",
          backgroundColor: "#1F1F20",
          padding: "16px",
        },
      }),
      key: "marketplace",
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {record.icon && (
            <img src={record.icon} alt={text} className="imgMarket" />
          )}
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: "Impressions",
      dataIndex: "impressions",
      className: "header-impressions",
      key: "impressions",
      width: "120px",
    },
    {
      title: "Clicks",
      dataIndex: "clicks",
      className: "header-clicks",
      width: "120px",
      key: "clicks",
    },
    {
      title: "Orders",
      dataIndex: "orders",
      className: "header-orders",
      width: "120px",
      key: "orders",
    },
    {
      title: "CVR",
      dataIndex: "cvr",
      className: "header-cvr",
      width: "120px",
      key: "cvr",
      render: (value) => (value ? `${value}%` : ""),
    },
    {
      title: "Sales",
      dataIndex: "sales",
      className: "header-sales",
      width: "120px",
      key: "sales",
      render: (value) => (value ? `$${Number(value).toLocaleString()}` : ""),
    },
    {
      title: "Sales Share",
      dataIndex: "salesShare",
      className: "header-sales-share",
      width: "120px",
      key: "salesShare",
      render: (value) => (value ? `${value}%` : ""),
    },
    {
      title: "Spend",
      dataIndex: "spend",
      className: "header-spend",
      width: "120px",
      key: "spend",
      render: (value) => (value ? `$${Number(value).toLocaleString()}` : ""),
    },
    {
      title: "ACOS",
      dataIndex: "acos",
      className: "header-acos",
      width: "120px",
      key: "acos",
      render: (value) => (value ? `${value}%` : ""),
    },
    {
      title: "ROI",
      dataIndex: "roi",
      className: "header-roi",
      width: "120px",
      key: "roi",
      render: (value) => (value ? `${value}%` : ""),
    },
    {
      title: "CPC",
      dataIndex: "cpc",
      className: "header-cpc",
      width: "120px",
      key: "cpc",
      render: (value) => (value ? `$${value}` : ""),
    },
    {
      title: "CAC",
      dataIndex: "cac",
      className: "header-cac",
      key: "cac",
      width: "120px",
      render: (value) => (value ? `$${value}` : ""),
    },
  ];

  // Filter columns based on visibility settings
  if (!visibleMetrics) {
    return allColumns;
  }

  return allColumns.filter((column) => {
    if (column.key === "marketplace") return true; // Always show marketplace column
    return visibleMetrics[column.key as keyof typeof visibleMetrics];
  });
}

export const data: DataType[] = [
  {
    key: "1",
    marketplace: "Amazon USA",
    icon: amazonIcon,
    impressions: 4161757,
    clicks: 116847,
    orders: 5910,
    cvr: "4",
    sales: "163608",
    salesShare: "96",
    spend: "7276",
    acos: "31",
    roi: "",
    cpc: "1.4",
    cac: "",
    children: [
      {
        key: "1-1",
        marketplace: "Amazon Ads",
        impressions: 1318290,
        clicks: 3724,
        orders: 529,
        cvr: "17",
        sales: "19011",
        salesShare: "10",
        spend: "6097",
        acos: "43",
        roi: "100",
        cpc: "2.25",
        cac: "13.2",
        children: [
          {
            key: "1-1-1",
            marketplace: "Sponsored Brands",
            impressions: 21666,
            clicks: 3561,
            orders: 505,
            cvr: "14",
            sales: "1259",
            salesShare: "1",
            spend: "629",
            acos: "45",
            roi: "98",
            cpc: "1.96",
            cac: "13.7",
          },
          {
            key: "1-1-2",
            marketplace: "Sponsored Products",
            impressions: 1206624,
            clicks: 26830,
            orders: 446,
            cvr: "17",
            sales: "13152",
            salesShare: "8",
            spend: "5968",
            acos: "44",
            roi: "106",
            cpc: "2.23",
            cac: "13.2",
          },
          {
            key: "1-1-3",
            marketplace: "Sponsored TV",
            impressions: 40000,
            clicks: 600,
            orders: 250,
            cvr: "42",
            sales: "8600",
            salesShare: "1",
            spend: "3000",
            acos: "35",
            roi: "151",
            cpc: "5.00",
            cac: "12.0",
          },
        ],
      },

      {
        key: "1-2",
        marketplace: "Amazon Attribution",
        impressions: 2351800,
        clicks: 117900,
        orders: 2157,
        cvr: "2",
        sales: "63841",
        salesShare: "39",
        spend: "519",
        acos: "1",
        roi: "321",
        cpc: "0.00",
        cac: "0.2",
        children: [
          {
            key: "1-2-1",
            marketplace: "Display - Other",
            impressions: 2359600,
            clicks: 116980,
            orders: 2594,
            cvr: "2",
            sales: "63951",
            salesShare: "39",
            spend: "519",
            acos: "1",
            roi: "321",
            cpc: "0.00",
            cac: "0.2",
          },
          {
            key: "1-2-2",
            marketplace: "Google Adwords",
            impressions: 720,
            clicks: 36,
            orders: 0,
            cvr: "0",
            sales: "0",
            salesShare: "0",
            spend: "0",
            acos: "",
            roi: "",
            cpc: "0.00",
            cac: "",
          },
          {
            key: "1-2-3",
            marketplace: "Instagram",
            impressions: 1480,
            clicks: 76,
            orders: 3,
            cvr: "4",
            sales: "90",
            salesShare: "0",
            spend: "0",
            acos: "0",
            roi: "323",
            cpc: "0.00",
            cac: "0.1",
          },
        ],
      },
      {
        key: "1-3",
        marketplace: "Amazon Organic/Other",
        impressions: 1041667,
        clicks: 20933,
        orders: 2504,
        cvr: "12",
        sales: "76456",
        salesShare: "45",
        spend: "",
        acos: "",
        roi: "325",
        cpc: "",
        cac: "",
        children: [
          {
            key: "1-3-1",
            marketplace: "Amazon Other",
            impressions: 2353960,
            clicks: 117698,
            orders: 2159,
            cvr: "2",
            sales: "63751",
            salesShare: "39",
            spend: "5179",
            acos: "1",
            roi: "321",
            cpc: "0.00",
            cac: "0.2",
          },
          {
            key: "1-3-2",
            marketplace: "Amazon Organic",
            impressions: 720,
            clicks: 36,
            orders: 0,
            cvr: "0",
            sales: "0",
            salesShare: "0",
            spend: "0",
            acos: "",
            roi: "",
            cpc: "0.00",
            cac: "",
          },
        ],
      },
    ],
  },
  {
    key: "2",
    marketplace: "Shopify",
    icon: ShopifyIcon,
    impressions: 89000,
    clicks: 1650,
    orders: 216,
    cvr: "13",
    sales: "6500",
    salesShare: "4",
    spend: "1500",
    acos: "23",
    roi: "21",
    cpc: "0.91",
    cac: "6.9",
    children: [
      {
        key: "2-1",
        marketplace: "Shopify",
        impressions: 89000,
        clicks: 1650,
        orders: 216,
        cvr: "13",
        sales: "6500",
        salesShare: "4",
        spend: "1500",
        acos: "23",
        roi: "210",
        cpc: "0.91",
        cac: "6.9",
        children: [
          {
            key: "2-1-1",
            marketplace: "Email",
            impressions: 60000,
            clicks: 3000,
            orders: 330,
            cvr: "11",
            sales: "10000",
            salesShare: "1",
            spend: "2000",
            acos: "20",
            roi: "225",
            cpc: "0.67",
            cac: "6.1",
          },
          {
            key: "2-1-2",
            marketplace: "Facebook Ads",
            impressions: 15000,
            clicks: 5500,
            orders: 830,
            cvr: "15",
            sales: "25000",
            salesShare: "2",
            spend: "7000",
            acos: "28",
            roi: "185",
            cpc: "1.27",
            cac: "8.4",
          },
          {
            key: "2-1-3",
            marketplace: "Google Ads",
            impressions: 68000,
            clicks: 8000,
            orders: 1000,
            cvr: "13",
            sales: "30000",
            salesShare: "2",
            spend: "6000",
            acos: "20",
            roi: "225",
            cpc: "0.75",
            cac: "6.0",
          },
        ],
      },
    ],
  },
  {
    key: "3",
    marketplace: "TikTok Shop",
    icon: tiktokIcon,
    impressions: null,
    clicks: null,
    orders: 1073,
    cvr: null,
    sales: "33843",
    salesShare: "2",
    spend: "5078",
    acos: "",
    roi: "25",
    cpc: "",
    cac: "4.7",
    children: [
      {
        key: "3-1",
        marketplace: "TikTok Ads",
        impressions: null,
        clicks: null,
        orders: 161,
        cvr: null,
        sales: "4948",
        salesShare: "0",
        spend: "1191",
        acos: "24",
        roi: "205",
        cpc: "",
        cac: "7.4",
        children: [
          {
            key: "3-1-1",
            marketplace: "TikTok Ads - Video",
            impressions: null,
            clicks: null,
            orders: 100,
            cvr: null,
            sales: "3000",
            salesShare: "0",
            spend: "800",
            acos: "27",
            roi: "275",
            cpc: "",
            cac: "8.0",
          },
          {
            key: "3-1-2",
            marketplace: "TikTok Ads - Carousel",
            impressions: null,
            clicks: null,
            orders: 61,
            cvr: null,
            sales: "1948",
            salesShare: "0",
            spend: "391",
            acos: "20",
            roi: "400",
            cpc: "",
            cac: "6.4",
          },
        ],
      },
      {
        key: "3-2",
        marketplace: "TikTok Organic/Other",
        impressions: null,
        clicks: null,
        orders: 912,
        cvr: null,
        sales: "28895",
        salesShare: "2",
        spend: "3887",
        acos: "13",
        roi: "256",
        cpc: "",
        cac: "4.3",
        children: [
          {
            key: "3-2-1",
            marketplace: "TikTok Organic - Video",
            impressions: null,
            clicks: null,
            orders: 500,
            cvr: null,
            sales: "15000",
            salesShare: "1",
            spend: "2000",
            acos: "13",
            roi: "250",
            cpc: "",
            cac: "4.0",
          },
          {
            key: "3-2-2",
            marketplace: "TikTok Organic - Carousel",
            impressions: null,
            clicks: null,
            orders: 412,
            cvr: null,
            sales: "13895",
            salesShare: "1",
            spend: "1887",
            acos: "13",
            roi: "256",
            cpc: "",
            cac: "4.3",
          },
        ],
      },
    ],
  },
  {
    key: "4",
    marketplace: "Total",
    impressions: 4250757,
    clicks: 118497,
    orders: 7199,
    cvr: "17",
    sales: "203951",
    salesShare: "100",
    spend: "13754",
    acos: "54",
    roi: "46",
    cpc: "2.31",
    cac: "11.6",
  },
];
